import { useCallback, useEffect, useState } from "react";
import { Check, ChevronDown, Eye, List, Loader2, Plus, X } from "lucide-react";

import { componentSource, runNode } from "@/lib/scoring/api";
import type { ComponentSource } from "@/lib/scoring/api";
import { tokenize } from "@/lib/scoring/highlight";
import { dependencies } from "@/lib/scoring/layout";
import type {
  ActorSpec,
  AnyNode,
  ContextNode,
  JsonSchema,
  NodeKind,
  ProjectionTarget,
  TriggerNode,
} from "@/lib/scoring/types";

import { CodeEditor } from "./CodeEditor";
import styles from "./Editor.module.css";

/** How long to wait after a keystroke before asking the server what an
 *  expression evaluates to. Long enough not to preview every character, short
 *  enough that the answer feels like it belongs to what you just typed. */


/**
 * The types a command input may declare.
 *
 * Four scalars plus two that hold their own shape. `object` is the JSON column:
 * a punch-stat blob, a shot chart, anything a sport needs that is not one
 * number. It carries a nested schema of its own, edited the same way, so a
 * blob is not an escape hatch that nothing can validate.
 */
/**
 * Where an action may write.
 *
 * Both of these are about this fixture and nothing else, and that is the whole
 * list. A program scores one match; a league table, a season total and a career
 * figure are the stat catalogue's, declared once and computed the same way for
 * every sport instead of written again in JavaScript per sport.
 *
 * ``classification_standing`` and ``participant_stat`` used to be offered here.
 * The publish gate refuses them now, and offering a road that is closed is worse
 * than not offering it — an author would build one, save it happily, and find
 * out at publish.
 */
const TARGETS: { value: ProjectionTarget; label: string; keys: string }[] = [
  { value: "stat_value", label: "Stat value", keys: "entry_id, stat_key, segment" },
  { value: "fixture_competitor_rank", label: "Competitor rank", keys: "entry_id, ranking_code" },
];

export interface NodeEditorProps {
  programId: string;
  kind: NodeKind;
  node: AnyNode;
  /** A scope to preview and test against — the fixture an author is working on. */
  scope: Record<string, unknown>;
  /** Role codes an actor may be mapped to, from `role_dictionary`. */
  roles: string[];
  /** Every other node's code in this program. A code is how one node names
   *  another, so two the same means a reference that could mean either. */
  taken: string[];
  /** The commands that run this node. Only rendered for a component, where the
   *  question "what else does this change?" is the whole point of the flag. */
  runsIn?: { id: string; code: string }[];
  /** Whether this node runs for the whole program rather than for named
   *  commands — true of a fold, which nothing references because every command
   *  folds through it. */
  everywhere?: boolean;
  /** Open one of those commands. */
  onOpenCommand?: (commandId: string) => void;
}

/**
 * The editing half of the inspector.
 *
 * Every field of the node that a person authors is here, because the alternative
 * — a canvas that shows a command and an editor that cannot change its input
 * schema — is a viewer wearing a builder's clothes.
 *
 * Two things it does that a plain form would not. An expression previews against
 * the server's evaluator while you type, because a second implementation in the
 * browser drifts, and the day it drifts the editor says a rule passes that the
 * engine rejects. And a function reports what it cost, so an author finds out
 * something is expensive while writing it rather than in production.
 *
 * What is *not* here: `reads`, and a command's `validations` and `triggers`.
 * Those are the connections, and they are drawn on the canvas — one way to say
 * a thing, so a line and a list can never disagree.
 */
export function NodeEditor({
  programId,
  kind,
  node,
  scope,
  roles,
  taken,
  runsIn = [],
  everywhere = false,
  onOpenCommand,
}: NodeEditorProps) {
  // The node as it is stored. Never edited — this panel reads.
  const draft = node as unknown as Record<string, unknown>;
  const [run, setRun] = useState<{ ok: boolean; text: string; cost: string } | null>(null);
  /** A run compiles the function into wasm and executes it against a fixture.
   *  That is not instant, and with no sign of it happening the only feedback a
   *  click gave was the old answer disappearing. */
  const [running, setRunning] = useState(false);

  // Nothing resets this on selection change, because the caller keys it by node
  // id and React remounts it. Copying the new node's values in an effect would
  // mean a render where the previous node's draft is showing under the new
  // node's name — which is how somebody saves a rule onto the wrong node.

  // The panel reads a node; nothing here writes one. Kept as a no-op so the
  // field components stay one shape whether they are showing or editing.
  // The panel reads a node; nothing here writes one. A no-op keeps the field
  // components one shape whether they are showing a value or editing it.
  // The panel reads a node; nothing here writes one. The patch is accepted and
  // discarded so the field components keep one shape whether they are showing a
  // value or editing it.
  const set = useCallback((patch: Record<string, unknown>) => void patch, []);

  const value = <T,>(key: string, fallback: T): T => (draft[key] as T) ?? fallback;
  const fn = value<string | null>("fn", null);
  const impl = value<string | null>("impl", null);

  /**
   * Which half of the rule is showing.
   *
   * UI state, not the document. It used to be read from the draft — an
   * expression meant `fn: null` — so the only way to show the function tab was
   * to blank the expression, and looking at the other tab destroyed what you
   * had written. Both halves now sit in the draft the whole time; this says
   * which one you are editing, and only that one is sent on save.
   */
  // Checked here rather than only at publish, because the failure is confusing
  // when it arrives late: two nodes share a name and a reference to it silently
  // means whichever the engine reaches first.
  const code = String(draft.code ?? "").trim();
  const codeProblem = !code
    ? "a node needs a code — it is how the canvas and other nodes name it"
    : taken.includes(code)
      ? `another node is already called ${code}`
      : null;

  const test = useCallback(async () => {
    setRun(null);
    setRunning(true);
    try {
      const result = await runNode(programId, node.id, scope);
      setRun({
        ok: result.ok,
        // Pretty-printed: the answer is a scoreboard or a row set, and one
        // squashed line of JSON is not something a person can check.
        text: result.ok ? JSON.stringify(result.value, null, 2) : (result.error ?? "failed"),
        cost: `${result.fuelUsed.toLocaleString()} fuel · ${result.wallMs.toFixed(1)}ms`,
      });
    } catch (error) {
      setRun({ ok: false, text: String(error), cost: "" });
    } finally {
      setRunning(false);
    }
  }, [programId, node.id, scope]);

  //: The editor is retired — behaviour lives in the flows plugin.
  const ro = true;

  return (
    <div className={styles.editorForm}>
      {/* A shared node, and what else it touches.

          First, above the code, because it changes what editing this *means*.
          Everything below is the same form it would be for a node one command
          runs — the difference is that saving it here rewrites how eight
          commands behave, and nothing else on this panel would say so. */}
      {node.component && (
        <SharedNote
          kind={kind}
          everywhere={everywhere}
          runsIn={runsIn}
          onOpenCommand={onOpenCommand}
        />
      )}

      {/* Code only. The name always mirrored it and could not be edited, so
          showing both was one fact twice. The problem message appears only
          when there is one — under the box it belongs to. */}
      <Section name="Code">
        <input
          className={styles.input}
          value={value("code", "")}
          disabled={ro}
          spellCheck={false}
          aria-label="Code"
          data-bad={codeProblem ? "true" : undefined}
          onChange={(e) => set({ code: e.target.value })}
        />
        {codeProblem && (
          <p className={styles.hint} data-bad="true">
            {codeProblem}
          </p>
        )}
      </Section>

      {/* Every kind, not just commands. The card shows this under its title, so
          a node without one is a box with a name and a count on it — which is
          all the canvas could ever tell you about what it is for. */}
      <Section name="Description">
        <textarea
          className={styles.textarea}
          rows={2}
          value={value("description", "")}
          disabled={ro}
          placeholder="What this is for, in a sentence."
          aria-label="Description"
          onChange={(e) => set({ description: e.target.value })}
        />
      </Section>

      {kind === "command" && (
        <CommandFields draft={draft} roles={roles} disabled={ro} set={set} />
      )}

      {kind === "validation" && impl && (
        <Section name="The check">
          <PluginSource impl={impl} />
        </Section>
      )}

      {kind === "validation" && !impl && (
        <Section name="The check">
          <p className={styles.hint}>
            This rule carries no flows component — it predates the plugin and cannot run.
          </p>
        </Section>
      )}

      {kind === "compute" && (
        <>
          {/* The key was not askable anywhere, and once a program has a second
              fold it is a publish failure to leave it out — so the only way to
              find out it was needed was to fail the gate. */}
          <Section name="What it owns">
            <label className={styles.field}>
              <span className={styles.label}>Name on the scoreboard</span>
              <input
                className={styles.input}
                value={value("key", "")}
                disabled={ro}
                spellCheck={false}
                placeholder="innings"
                onChange={(e) => set({ key: e.target.value })}
              />
            </label>
            <p className={styles.hint} data-spaced="true">
              Rules read this back as <code>derived.{value("key", "") || "…"}</code>. Two folds
              cannot share a name; leaving it blank claims the whole scoreboard, which only works
              while there is exactly one fold.
            </p>
          </Section>

          <Section name="The function">
            <p className={styles.hint} data-spaced="true">
              Reads the timeline, returns the state everything else judges against.
            </p>
            {impl ? (
              <PluginSource impl={impl} />
            ) : (
              <CodeEditor value={fn ?? ""} rows={14} disabled={ro} onChange={(v) => set({ fn: v })} />
            )}
          </Section>
        </>
      )}

      {kind === "trigger" && (
        <TriggerFields draft={draft} node={node as TriggerNode} disabled={ro} set={set} />
      )}

      {kind === "context" && (
        <Section name="The function">
          {impl ? (
            <PluginSource impl={impl} />
          ) : (
            <CodeEditor value={fn ?? ""} rows={12} disabled={ro} onChange={(v) => set({ fn: v })} />
          )}
        </Section>
      )}

      {/* A block like the rest of the form, so the button and its answer are
          not loose against the panel edge. */}
      {kind !== "command" && (
        <Section name="Try it">
          <div className={styles.preview}>
            <button
              className={styles.button}
              type="button"
              disabled={running}
              onClick={test}
            >
              {running && <Loader2 size={13} className={styles.spinner} />}
              {running ? "Running…" : "Run"}
            </button>
            {run && (
              <>
                <span className={styles.verdict} data-holds={run.ok ? "true" : "error"}>
                  {run.ok ? "ran" : "threw"}
                </span>
                <span className={styles.cost}>{run.cost}</span>
              </>
            )}
          </div>

          {/* The whole answer, laid out and coloured — a scoreboard squashed
              onto one truncated line was not something anyone could check. */}
          {run && (
            <pre className={styles.runBox} data-bad={!run.ok || undefined}>
              <code className={styles.tokenized}>
                {run.ok
                  ? tokenize(run.text).map((token, i) => (
                      <span key={i} data-tok={token.kind}>
                        {token.text}
                      </span>
                    ))
                  : run.text}
              </code>
            </pre>
          )}
        </Section>
      )}

    </div>
  );
}

/* ------------------------------------------------------------------ command */

/**
 * A command's two essential fields, and its actors.
 *
 * The code is what the command *is* — `cricket.ball` — and the input schema is
 * what a scorer sends with it. Neither could be authored before this, which is
 * the whole reason a command node was previously something you could look at
 * and not build.
 */
function CommandFields({
  draft,
  roles,
  disabled,
  set,
}: {
  draft: Record<string, unknown>;
  roles: string[];
  disabled: boolean;
  set: (patch: Record<string, unknown>) => void;
}) {
  // The rows live here rather than being read back out of the schema each
  // render, because a row is added before it is named and `toSchema` drops
  // unnamed ones — derived rows would make "Add a field" appear to do nothing.
  const [fields, setFields] = useState<Field[]>(() =>
    readSchema((draft.input as JsonSchema) ?? {}),
  );
  const actors = (draft.actors as ActorSpec[]) ?? [];

  const [asZod, setAsZod] = useState(false);
  const [copied, setCopied] = useState(false);

  const writeFields = (next: Field[]) => {
    setFields(next);
    set({ input: writeSchema(next) });
  };

  return (
    <>
      <Section
        name={asZod ? "Generated schema" : "Input parameters"}
        aside={
          asZod
            ? undefined
            : `${countFieldsIn(fields)} field${countFieldsIn(fields) === 1 ? "" : "s"}`
        }
        action={
          <>
            {asZod && (
              <button
                type="button"
                className={styles.copySchema}
                onClick={() => {
                  void navigator.clipboard
                    ?.writeText(JSON.stringify(writeSchema(fields), null, 2))
                    .then(() => setCopied(true));
                  window.setTimeout(() => setCopied(false), 1200);
                }}
              >
                {copied ? "Copied" : "Copy"}
              </button>
            )}
            <button
              type="button"
              className={styles.peek}
              data-on={asZod || undefined}
              aria-pressed={asZod}
              title={asZod ? "Back to fields" : "View schema"}
              aria-label="Toggle the generated schema"
              onClick={() => setAsZod((on) => !on)}
            >
              {asZod ? <List size={14} /> : <Eye size={14} />}
            </button>
          </>
        }
      >
        {asZod ? (
          <SchemaView fields={fields} />
        ) : (
          <>
            <FieldTree fields={fields} disabled={disabled} onChange={writeFields} />
            {fields.length === 0 && (
              <p className={styles.emptyFields}>No parameters yet. Add one to start.</p>
            )}
            {!disabled && (
              <div className={styles.rootAdd}>
                <button
                  type="button"
                  className={styles.add}
                  title="Add field"
                  aria-label="Add field"
                  onClick={() => writeFields([...fields, newField()])}
                >
                  <Plus size={13} />
                </button>
              </div>
            )}
          </>
        )}
      </Section>

      {/* The tick means *required* here, exactly as it does one section up. It
          used to be a box labelled "optional", which put the same-looking
          control next to the same-looking control meaning the opposite thing —
          so the flag is stored inverted and read the other way round. */}
      <Section name="Participants" aside={actors.length ? `${actors.length}` : undefined}>
        <p className={styles.blurb}>
          Which input field holds a participant, and their part. Without this, nothing it writes is
          theirs.
        </p>

        {actors.map((actor, index) => (
          <div key={index} className={styles.actorRow}>
            <div className={styles.pickWrap}>
              <select
                className={styles.pick}
                data-empty={!actor.path || undefined}
                value={actor.path}
                disabled={disabled}
                aria-label="Input field"
                onChange={(e) =>
                  set({
                    actors: actors.map((a, i) => (i === index ? { ...a, path: e.target.value } : a)),
                  })
                }
              >
                <option value="">Pick a field</option>
                {fields.map((field) => (
                  <option key={field.name} value={`input.${field.name}`}>
                    input.{field.name}
                  </option>
                ))}
              </select>
              <ChevronDown size={12} />
            </div>

            <div className={styles.pickWrap}>
              <select
                className={styles.pick}
                data-empty={!actor.role || undefined}
                value={actor.role}
                disabled={disabled}
                aria-label="Role"
                onChange={(e) =>
                  set({
                    actors: actors.map((a, i) => (i === index ? { ...a, role: e.target.value } : a)),
                  })
                }
              >
                <option value="">Pick a role</option>
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
              <ChevronDown size={12} />
            </div>

            <button
              type="button"
              className={styles.req}
              data-on={!actor.optional || undefined}
              disabled={disabled}
              aria-pressed={!actor.optional}
              aria-label={`${actor.role || "This participant"} is required`}
              title={actor.optional ? "May be absent" : "Required"}
              onClick={() =>
                set({
                  actors: actors.map((a, i) =>
                    i === index ? { ...a, optional: !a.optional } : a,
                  ),
                })
              }
            >
              <Check size={11} />
            </button>

            {!disabled && (
              <button
                type="button"
                className={styles.del}
                aria-label={`Remove ${actor.role || "participant"}`}
                title="Remove"
                onClick={() => set({ actors: actors.filter((_, i) => i !== index) })}
              >
                <X size={13} />
              </button>
            )}
          </div>
        ))}

        {!disabled && (
          <div className={styles.rootAdd}>
            <button
              type="button"
              className={styles.add}
              disabled={fields.length === 0}
              title={fields.length === 0 ? "Add an input field first" : "Add a participant"}
              aria-label="Add a participant"
              onClick={() =>
                set({
                  actors: [
                    ...actors,
                    { path: "", role: roles[0] ?? "", kind: "participant", optional: false },
                  ],
                })
              }
            >
              <Plus size={13} />
            </button>
          </div>
        )}
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ trigger */

function TriggerFields({
  draft,
  node,
  disabled,
  set,
}: {
  draft: Record<string, unknown>;
  node: TriggerNode;
  disabled: boolean;
  set: (patch: Record<string, unknown>) => void;
}) {
  const owns = (draft.owns as TriggerNode["owns"]) ?? null;
  const target = TARGETS.find((t) => t.value === owns?.target);

  return (
    <>
      <Section name="When it runs">
        <div className={styles.pair}>
          <label className={styles.field}>
            <span className={styles.label}>Timing</span>
            <select
              className={styles.input}
              value={String(draft.mode ?? "sync")}
              disabled={disabled}
              onChange={(e) => set({ mode: e.target.value })}
            >
              <option value="sync">Sync — inside the command</option>
              <option value="async">Async — queued after commit</option>
            </select>
          </label>
        </div>
      </Section>

      <Section name="What it produces">
        <label className={styles.field}>
          <span className={styles.label}>Where the rows go</span>
          <select
            className={styles.input}
            value={owns?.target ?? ""}
            disabled={disabled}
            onChange={(e) =>
              set({
                owns: e.target.value
                  ? {
                      target: e.target.value,
                      // The engine fixes the key columns per target; sending
                      // anything else is a publish failure, so they are not
                      // asked for.
                      keys: (TARGETS.find((t) => t.value === e.target.value)?.keys ?? "")
                        .split(", ")
                        .filter(Boolean),
                      statKeys: owns?.statKeys ?? [],
                    }
                  : null,
              })
            }
          >
            <option value="">Nothing — computes only</option>
            {TARGETS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        {target && (
          <>
            <label className={`${styles.field} ${styles.fieldSpaced}`}>
              <span className={styles.label}>Stat keys it owns</span>
              <input
                className={styles.input}
                value={(owns?.statKeys ?? []).join(", ")}
                disabled={disabled}
                spellCheck={false}
                placeholder="runs, balls_faced, fours"
                onChange={(e) =>
                  set({
                    owns: {
                      ...owns,
                      target: owns?.target,
                      keys: owns?.keys ?? [],
                      statKeys: e.target.value
                        .split(",")
                        .map((key) => key.trim())
                        .filter(Boolean),
                    },
                  })
                }
              />
            </label>
          </>
        )}
      </Section>

      <Section name="The function">
        <Reads node={node} />
        {(draft.impl as string | null) ? (
          <PluginSource impl={draft.impl as string} />
        ) : (
          <CodeEditor
            value={(draft.fn as string) ?? ""}
            rows={14}
            disabled={disabled}
            onChange={(v) => set({ fn: v })}
          />
        )}
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------- pieces */

/**
 * What else this edit changes.
 *
 * A component looks exactly like any other node once it is open, and that is
 * the problem: the form gives no sign that the rule you are about to loosen is
 * the one eight commands lean on. Naming the flows is the cheapest possible
 * version of that warning, and naming them as buttons means checking one costs
 * a click rather than a hunt across the canvas.
 *
 * Three states, because "nowhere" and "everywhere" are both real and neither is
 * a count. A fold runs for the whole program and nothing references it; a
 * component made a minute ago runs nowhere yet, and saying "0 commands" would
 * read as a fault rather than as the next thing to do.
 */
function SharedNote({
  kind,
  everywhere,
  runsIn,
  onOpenCommand,
}: {
  kind: NodeKind;
  everywhere: boolean;
  runsIn: { id: string; code: string }[];
  onOpenCommand?: (commandId: string) => void;
}) {
  const what = kind === "validation" ? "rule" : kind === "trigger" ? "action" : "node";
  return (
    <aside
      className={styles.shared}
      data-reach={everywhere ? "all" : runsIn.length === 0 ? "none" : "some"}
    >
      <p className={styles.sharedHead}>
        <List size={12} />
        Component
      </p>
      {everywhere ? (
        <p className={styles.sharedWhat}>
          This {what} runs for the whole program. Every command folds through it, so there is
          nothing to attach it to — and an edit here changes every one of them.
        </p>
      ) : runsIn.length === 0 ? (
        <p className={styles.sharedWhat}>
          Not on any command yet. Fill it in, then open <strong>Components</strong> in the left
          menu and drop it on the commands that should run it.
        </p>
      ) : (
        <>
          <p className={styles.sharedWhat}>
            Saving changes {runsIn.length === 1 ? "this command" : `these ${runsIn.length} commands`}
            :
          </p>
          <div className={styles.sharedFlows}>
            {runsIn.map((command) => (
              <button
                key={command.id}
                type="button"
                className={styles.sharedFlow}
                title={`Open ${command.code}`}
                disabled={!onOpenCommand}
                onClick={() => onOpenCommand?.(command.id)}
              >
                {command.code}
              </button>
            ))}
          </div>
        </>
      )}
    </aside>
  );
}

/**
 * The Python behind an impl node, read straight off the installed plugin.
 *
 * The document names a component; the code lives in omnium-flows. This shows
 * it read-only where the editable function used to sit, so "what does this
 * node do" has an answer on the screen that asks it — and the caption says
 * where to go to change it, because that is the question right after.
 */
function PluginSource({ impl }: { impl: string }) {
  // Keyed by impl, so switching nodes never shows the previous node's code —
  // and nothing resets synchronously inside the effect.
  const [loaded, setLoaded] = useState<{ impl: string; s: ComponentSource } | null>(null);
  const [error, setError] = useState<{ impl: string; message: string } | null>(null);

  useEffect(() => {
    let stale = false;
    componentSource(impl)
      .then((s) => !stale && setLoaded({ impl, s }))
      .catch(
        (e) =>
          !stale && setError({ impl, message: e instanceof Error ? e.message : String(e) }),
      );
    return () => {
      stale = true;
    };
  }, [impl]);

  const current = loaded?.impl === impl ? loaded.s : null;
  const currentError = error?.impl === impl ? error.message : null;

  if (currentError) {
    return (
      <p className={styles.hint} data-bad="true">
        {impl}: {currentError}
      </p>
    );
  }
  if (!current) {
    return (
      <p className={styles.hint}>
        <Loader2 size={13} className={styles.spinner} /> {impl}
      </p>
    );
  }
  return (
    <div>
      <p className={styles.hint} data-spaced="true">
        <code>{current.key}</code> — lives in the omnium-flows plugin at{" "}
        <code>
          {current.module}:{current.line}
        </code>
        {current.inheritedFrom ? (
          <>
            {" "}
            (inherited from <code>{current.inheritedFrom}</code>)
          </>
        ) : null}
        . Change it there and republish; nothing here is editable.
      </p>
      <CodeEditor
        value={current.source}
        rows={Math.min(24, current.source.split("\n").length + 1)}
        disabled
        onChange={() => {}}
      />
    </div>
  );
}

function Section({
  name,
  aside,
  action,
  children,
}: {
  name: string;
  aside?: string;
  /** Controls that belong to the section rather than to any row in it. */
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.block}>
      <h3 className={styles.blockName}>
        {name}
        {(aside || action) && (
          <span className={styles.blockAside}>
            {aside}
            {action}
          </span>
        )}
      </h3>
      {children}
    </section>
  );
}


/** What this node reads that another produces. Read-only: it is a connection,
 *  and connections are drawn. */
function Reads({ node }: { node: TriggerNode | ContextNode }) {
  const from = dependencies(node);
  if (from.length === 0) return null;
  return (
    <div className={`${styles.chips} ${styles.readsRow}`}>
      <span className={styles.reads}>reads</span>
      {from.map((ref) => (
        <span key={ref} className={styles.chip} data-kind="trigger">
          {ref}
        </span>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------- input params */

/** What a field can be, in the words the form uses.
 *
 * `Int` and `Float` rather than one `Number`: four seeded fields are integers,
 * and folding them together would silently widen a published command's contract
 * from "a count" to "any number". Short names, because the type sits in the
 * narrowest column on the row.
 */
const TYPES = ["Text", "Int", "Float", "Boolean", "Date", "JSON", "Array"] as const;
type TypeName = (typeof TYPES)[number];

/** The two that hold other fields. */
const nests = (type: TypeName) => type === "JSON" || type === "Array";

interface Field {
  id: string;
  name: string;
  type: TypeName;
  required: boolean;
  open: boolean;
  children: Field[];
}

let seq = 0;
const newField = (): Field => ({
  id: `f${++seq}`,
  name: "",
  type: "Text",
  required: false,
  open: true,
  children: [],
});

/** A stored schema as the tree the form edits. */
function readSchema(schema: JsonSchema): Field[] {
  const required = new Set(schema.required ?? []);
  return Object.entries(schema.properties ?? {}).map(([name, property]) =>
    readField(name, property as JsonSchema, required.has(name)),
  );
}

function readField(name: string, property: JsonSchema, isRequired: boolean): Field {
  const raw = Array.isArray(property.type) ? property.type[0] : property.type;
  const kids = (inner: JsonSchema) => readSchema(inner);
  let type: TypeName = "Text";
  let children: Field[] = [];

  if (raw === "object") {
    type = "JSON";
    children = kids(property);
  } else if (raw === "array") {
    type = "Array";
    children = kids((property.items ?? {}) as JsonSchema);
  } else if (raw === "integer") {
    type = "Int";
  } else if (raw === "number") {
    type = "Float";
  } else if (raw === "boolean") {
    type = "Boolean";
  } else if (property.format === "date-time") {
    type = "Date";
  }

  return { id: `f${++seq}`, name, type, required: isRequired, open: true, children };
}

/** And back. Unnamed rows are dropped rather than written as `""`, so a
 *  half-typed field cannot become a property nothing can reference. */
function writeSchema(fields: Field[]): JsonSchema {
  const properties: Record<string, JsonSchema> = {};
  const required: string[] = [];
  for (const field of fields) {
    const key = field.name.trim();
    if (!key) continue;
    properties[key] = writeField(field);
    if (field.required) required.push(key);
  }
  return { type: "object", properties, required };
}

function writeField(field: Field): JsonSchema {
  if (field.type === "JSON") return writeSchema(field.children);
  if (field.type === "Array") return { type: "array", items: writeSchema(field.children) };
  if (field.type === "Date") return { type: "string", format: "date-time" };
  if (field.type === "Int") return { type: "integer" };
  if (field.type === "Float") return { type: "number" };
  if (field.type === "Boolean") return { type: "boolean" };
  return { type: "string" };
}

function countFieldsIn(list: Field[]): number {
  return list.reduce((n, f) => n + 1 + countFieldsIn(f.children), 0);
}

/** Names that clash with a sibling. A schema cannot hold the same key twice, so
 *  the second one silently wins — which is why it is marked rather than left to
 *  be discovered when a command drops a value. */
function clashing(list: Field[]): Set<string> {
  const seen = new Map<string, number>();
  for (const f of list) {
    const name = f.name.trim();
    if (name) seen.set(name, (seen.get(name) ?? 0) + 1);
  }
  return new Set([...seen].filter(([, n]) => n > 1).map(([name]) => name));
}

/**
 * The fields of a command's input, as a thread.
 *
 * A schema is a tree, and a tree drawn only by indentation cannot be read back
 * to its parent past the second level. Each nested group hangs off a rail with
 * an elbow into every row, the way a comment thread does.
 */
function FieldTree({
  fields,
  disabled,
  onChange,
}: {
  fields: Field[];
  disabled: boolean;
  onChange: (next: Field[]) => void;
}) {
  const clashes = clashing(fields);

  const edit = (id: string, patch: Partial<Field>) =>
    onChange(fields.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const addAfter = (id: string) => {
    const at = fields.findIndex((f) => f.id === id);
    const next = [...fields];
    next.splice(at + 1, 0, newField());
    onChange(next);
  };

  return (
    <div className={styles.tree}>
      {fields.map((field) => (
        <div key={field.id} className={styles.fieldNode}>
          <div className={styles.fieldRow}>
            <div className={styles.nameWrap}>
              {nests(field.type) && (
                <button
                  type="button"
                  className={styles.caret}
                  data-closed={!field.open || undefined}
                  aria-label={field.open ? "Collapse" : "Expand"}
                  aria-expanded={field.open}
                  onClick={() => edit(field.id, { open: !field.open })}
                >
                  <ChevronDown size={11} />
                </button>
              )}
              <input
                className={styles.fieldName}
                data-inset={nests(field.type) || undefined}
                data-clash={clashes.has(field.name.trim()) || undefined}
                value={field.name}
                placeholder="name"
                spellCheck={false}
                disabled={disabled}
                aria-label="Field name"
                onChange={(event) => edit(field.id, { name: event.target.value })}
                onKeyDown={(event) => {
                  // Enter adds the next one, Backspace on an empty row removes
                  // it: filling in a schema is typing, and reaching for the
                  // mouse between every field is the slow part.
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addAfter(field.id);
                  }
                  if (event.key === "Backspace" && !field.name && !field.children.length) {
                    event.preventDefault();
                    onChange(fields.filter((f) => f.id !== field.id));
                  }
                }}
              />
            </div>

            <div className={styles.typeWrap}>
              <select
                className={styles.fieldType}
                value={field.type}
                disabled={disabled}
                aria-label="Field type"
                onChange={(event) => {
                  const type = event.target.value as TypeName;
                  const wasNested = nests(field.type);
                  edit(field.id, {
                    type,
                    open: true,
                    // A shape you have just asked for arrives with somewhere to
                    // type, rather than as an empty box you then have to open.
                    children: nests(type)
                      ? wasNested && field.children.length
                        ? field.children
                        : [newField()]
                      : [],
                  });
                }}
              >
                {TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <ChevronDown size={12} />
            </div>

            <button
              type="button"
              className={styles.req}
              data-on={field.required || undefined}
              disabled={disabled}
              aria-pressed={field.required}
              aria-label={`${field.name || "This field"} is required`}
              title={field.required ? "Required" : "Optional"}
              onClick={() => edit(field.id, { required: !field.required })}
            >
              <Check size={11} />
            </button>

            {!disabled && (
              <button
                type="button"
                className={styles.del}
                aria-label={`Remove ${field.name || "field"}`}
                title="Remove"
                onClick={() => onChange(fields.filter((f) => f.id !== field.id))}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {nests(field.type) && field.open && (
            <div className={styles.kids}>
              <FieldTree
                fields={field.children}
                disabled={disabled}
                onChange={(children) => edit(field.id, { children })}
              />
              {!disabled && (
                <div className={`${styles.fieldNode} ${styles.addRow}`}>
                  <button
                    type="button"
                    className={styles.add}
                    title="Add nested field"
                    aria-label="Add nested field"
                    onClick={() => edit(field.id, { children: [...field.children, newField()] })}
                  >
                    <Plus size={13} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** The generated JSON Schema, coloured. Read-only: the form is where a schema
 *  is written, and two places to write one is two places for them to disagree. */
function SchemaView({ fields }: { fields: Field[] }) {
  const json = JSON.stringify(writeSchema(fields), null, 2);
  const lines = json.split("\n");
  return (
    <div className={styles.codeEditor} data-readonly="true">
      <div className={styles.codeGutter} aria-hidden="true">
        {lines.map((_, i) => (
          <span key={i}>{i + 1}</span>
        ))}
      </div>
      <div className={styles.codeArea}>
        <pre className={styles.schema}>
          <code>
            {lines.map((line, i) => (
              <div key={i}>{colourJson(line) as React.ReactNode[]}</div>
            ))}
          </code>
        </pre>
      </div>
    </div>
  );
}

function colourJson(line: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const pattern = /("(?:[^"\\]|\\.)*"\s*:)|("(?:[^"\\]|\\.)*")|(\btrue\b|\bfalse\b)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(line))) {
    if (match.index > last) out.push(line.slice(last, match.index));
    const [text, key, str, bool] = match;
    const cls = key ? styles.jsonKey : str ? styles.jsonString : styles.jsonBool;
    out.push(
      <span key={`${match.index}-${text}`} className={cls}>
        {text}
      </span>,
    );
    last = match.index + text.length;
    void bool;
  }
  if (last < line.length) out.push(line.slice(last));
  return out;
}
